import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to format API error
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
};

// Users
export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getSameBl: () => api.get('/users/same-bl'),
};

// Business Units
export const businessUnitsApi = {
  getAll: () => api.get('/business-units'),
  create: (data) => api.post('/business-units', data),
  update: (id, data) => api.put(`/business-units/${id}`, data),
  delete: (id) => api.delete(`/business-units/${id}`),
};

// Business Lines
export const businessLinesApi = {
  getAll: (businessUnitId) => api.get('/business-lines', { params: { business_unit_id: businessUnitId } }),
  create: (data) => api.post('/business-lines', data),
  update: (id, data) => api.put(`/business-lines/${id}`, data),
  delete: (id) => api.delete(`/business-lines/${id}`),
};

// Teams
export const teamsApi = {
  getAll: (businessLineId) => api.get('/teams', { params: { business_line_id: businessLineId } }),
  getOne: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
};

// Schedules
export const schedulesApi = {
  getAll: (params) => api.get('/schedules', { params }),
  getCalendar: (month, year, teamId, view) => api.get('/schedules/calendar', { params: { month, year, team_id: teamId, view: view || 'team' } }),
  getDetail: (id) => api.get(`/schedules/${id}`),
  create: (data) => api.post('/schedules', data),
  createRecurring: (data) => api.post('/schedules/recurring', data),
  update: (id, data) => api.put(`/schedules/${id}`, data),
  delete: (id) => api.delete(`/schedules/${id}`),
  bulkDelete: (ids) => api.post('/schedules/bulk-delete', { ids }),
  requestCancellation: (id, reason) => api.post(`/schedules/${id}/cancel-request`, { reason }),
  onCallRotation: (data) => api.post('/schedules/on-call-rotation', data),
  getOnCallRotations: (teamId) => api.get('/schedules/on-call-rotations', { params: { team_id: teamId } }),
  autoPlan: (data) => api.post('/schedules/auto-plan', data),
  applyAutoPlan: (suggestions) => api.post('/schedules/auto-plan/apply', suggestions),
};

// Shift Swaps
export const shiftSwapsApi = {
  getAll: () => api.get('/shift-swaps'),
  create: (data) => api.post('/shift-swaps', data),
  respond: (id, action) => api.put(`/shift-swaps/${id}/respond`, { action }),
  confirm: (id, action) => api.put(`/shift-swaps/${id}/confirm`, { action }),
};

// File Uploads
export const uploadsApi = {
  upload: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  getUrl: (fileId) => `${API_URL}/api/uploads/${fileId}`,
};

// Notifications
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markAllRead: () => api.put('/notifications/read-all'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
};

// Employee History
export const employeeHistoryApi = {
  get: (userId) => api.get(`/employees/${userId}/history`),
};

// Company Holidays
export const holidaysApi = {
  getAll: (year) => api.get('/company-holidays', { params: { year } }),
  create: (data) => api.post('/company-holidays', data),
  delete: (id) => api.delete(`/company-holidays/${id}`),
  bulkCreate: (holidays) => api.post('/company-holidays/bulk', holidays),
};

// Approvals
export const approvalsApi = {
  getAll: (params) => api.get('/approval-requests', { params }),
  getPending: () => api.get('/approval-requests/pending'),
  action: (id, action, comments) => api.post(`/approval-requests/${id}/action`, { action, comments }),
  bulkDelete: (ids) => api.post('/approval-requests/bulk-delete', { ids }),
};

// Workflow Configs
export const workflowConfigsApi = {
  getAll: () => api.get('/approval-workflow-configs'),
  create: (data) => api.post('/approval-workflow-configs', data),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Activity Logs
export const activityLogsApi = {
  getAll: (params) => api.get('/activity-logs', { params }),
};

// Reports
export const reportsApi = {
  scheduleSummary: (params) => api.get('/reports/schedule-summary', { params }),
  teamAttendance: (teamId, month, year) => api.get('/reports/team-attendance', { params: { team_id: teamId, month, year } }),
  approvalStats: (params) => api.get('/reports/approval-stats', { params }),
  monthly: (params) => api.get('/reports/monthly', { params }),
};

// Anomaly Reports
export const anomalyApi = {
  getAll: (params) => api.get('/anomaly-reports', { params }),
  create: (data) => api.post('/anomaly-reports', data),
  resolve: (id, data) => api.post(`/anomaly-reports/${id}/resolve`, data),
  bulkDelete: (ids) => api.post('/anomaly-reports/bulk-delete', { ids }),
};

// Hour Movements
export const hourMovementsApi = {
  getByUser: (userId) => api.get(`/hour-movements/${userId}`),
  getAll: (params) => api.get('/hour-movements', { params }),
};

// Presence Constraints
export const constraintsApi = {
  getAll: () => api.get('/presence-constraints'),
  create: (data) => api.post('/presence-constraints', data),
};

// Balances
export const balancesApi = {
  getAll: (params) => api.get('/balances', { params }),
  bulkUpdate: (updates) => api.post('/balances/bulk-update', { updates }),
};

// Exclusion Rules
export const exclusionRulesApi = {
  getAll: () => api.get('/exclusion-rules'),
  create: (typeA, typeB, description) => api.post('/exclusion-rules', { type_a: typeA, type_b: typeB, description }),
  update: (id, enabled) => api.put(`/exclusion-rules/${id}`, { enabled }),
  delete: (id) => api.delete(`/exclusion-rules/${id}`),
};

// System Settings
export const systemSettingsApi = {
  getAll: () => api.get('/system-settings'),
  update: (key, value) => api.put(`/system-settings/${key}`, { value }),
};

// Email
export const emailApi = {
  testEmail: (toEmail) => api.post('/email/test', { to_email: toEmail }),
  getTemplates: () => api.get('/email-templates'),
  updateTemplate: (key, data) => api.put(`/email-templates/${key}`, data),
  resetTemplate: (key) => api.delete(`/email-templates/${key}`),
  previewTemplate: (key) => api.post(`/email-templates/${key}/preview`),
  getLogs: (limit = 100) => api.get('/email-logs', { params: { limit } }),
};

// Admin
export const adminApi = {
  cleanupOrphans: () => api.post('/admin/cleanup-orphans'),
};

// Auth extras
export const authExtrasApi = {
  entraLogin: () => api.get('/auth/entra-login'),
  entraStatus: () => api.get('/auth/entra-status'),
  registrationStatus: () => api.get('/auth/registration-status'),
  resetPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { new_password: newPassword }),
  listEntraUsers: (search) => api.get('/entra-users', { params: { search } }),
  importEntraUsers: (users) => api.post('/entra-users/import', users),
};

export default api;
